import { useEffect } from "react";
import { useSearch } from "wouter";
import { logger } from "@/lib/logger";

const REFERRAL_CODE_KEY = "pending_referral_code";

export function captureReferralCode(): string | null {
  const params = new URLSearchParams(window.location.search);
  const refCode = params.get("ref");

  if (refCode) {
    localStorage.setItem(REFERRAL_CODE_KEY, refCode);
    return refCode;
  }

  return localStorage.getItem(REFERRAL_CODE_KEY);
}

export function getPendingReferralCode(): string | null {
  return localStorage.getItem(REFERRAL_CODE_KEY);
}

export function clearPendingReferralCode(): void {
  localStorage.removeItem(REFERRAL_CODE_KEY);
}

export async function processReferralConversion(): Promise<boolean> {
  const referralCode = getPendingReferralCode();
  if (!referralCode) {
    return false;
  }

  try {
    const response = await fetch("/api/referrals/convert", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${localStorage.getItem("auth_token") || ""}`,
      },
      body: JSON.stringify({ referralCode }),
    });

    if (response.ok) {
      clearPendingReferralCode();
      logger.log("[Referral] Successfully processed referral conversion");
      return true;
    } else {
      const data = await response.json();
      logger.warn("[Referral] Conversion failed:", data.error);
      if (data.error === "already_referred" || data.error === "invalid_code") {
        clearPendingReferralCode();
      }
      return false;
    }
  } catch (error) {
    logger.error("[Referral] Error processing conversion:", error);
    return false;
  }
}

export function useReferralCapture() {
  const searchString = useSearch();

  useEffect(() => {
    const params = new URLSearchParams(searchString);
    const refCode = params.get("ref");

    if (refCode) {
      localStorage.setItem(REFERRAL_CODE_KEY, refCode);
      logger.log("[Referral] Captured referral code:", refCode);

      fetch(`/api/referrals/click/${refCode}`, { method: "POST" }).catch(
        () => {}
      );
    }
  }, [searchString]);

  return {
    pendingReferralCode: getPendingReferralCode(),
    processConversion: processReferralConversion,
    clearCode: clearPendingReferralCode,
  };
}
